/**
 * useCountryDetection - Simple React hook for timezone-based country detection
 * Replaces complex IP/CloudFlare detection with instant timezone detection
 */

import { useState, useEffect } from 'react';
import { getCountryFromTimezone } from '@/utils/simpleLocationDetection';

export interface UseCountryDetectionResult {
  countryCode: string;
  isLoading: boolean;
  error: string | null;
  isSupported: boolean;
  displayName: string;
  setManualCountry: (country: string) => void;
  clearCache: () => void;
  // Simplified features
  confidence: number;
  detectionSource: 'timezone' | 'manual' | 'fallback';
  refresh: () => Promise<void>;
}

export interface UseCountryDetectionOptions {
  // Simplified options - most options removed since timezone detection is instant
  fallbackCountry?: string;
}

/**
 * Simple country name mapping for common countries
 */
const COUNTRY_NAMES: Record<string, string> = {
  'US': 'United States',
  'IN': 'India',
  'NP': 'Nepal',
  'BD': 'Bangladesh',
  'LK': 'Sri Lanka',
  'PK': 'Pakistan',
  'GB': 'United Kingdom',
  'DE': 'Germany',
  'FR': 'France',
  'CN': 'China',
  'JP': 'Japan',
  'KR': 'South Korea',
  'AU': 'Australia',
  'CA': 'Canada',
  'BR': 'Brazil',
};

/**
 * Supported countries with full features
 */
const SUPPORTED_COUNTRIES = ['IN', 'NP', 'US', 'GB', 'CA', 'AU'];

export function useCountryDetection(options: UseCountryDetectionOptions = {}): UseCountryDetectionResult {
  const { fallbackCountry = 'US' } = options;

  const [countryCode, setCountryCode] = useState<string>(fallbackCountry);
  const [isLoading, setIsLoading] = useState<boolean>(false); // No loading for instant timezone detection
  const [error, setError] = useState<string | null>(null);
  const [detectionSource, setDetectionSource] = useState<'timezone' | 'manual' | 'fallback'>('timezone');

  // Detect country on component mount
  useEffect(() => {
    detectUserCountry();
  }, []);

  const detectUserCountry = async () => {
    try {
      setError(null);

      // Use instant timezone detection
      const detected = getCountryFromTimezone();
      setCountryCode(detected);
      setDetectionSource('timezone');

      console.log(`[useCountryDetection] ✅ Timezone detection: ${detected}`);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Country detection failed';
      setError(errorMessage);
      setCountryCode(fallbackCountry);
      setDetectionSource('fallback');

      console.warn(`[useCountryDetection] ❌ Detection failed, using fallback: ${fallbackCountry}`);
    }
  };

  // Manual country override
  const setManualCountry = (country: string) => {
    try {
      setCountryCode(country.toUpperCase());
      setDetectionSource('manual');
      setError(null);

      console.log(`[useCountryDetection] 🔧 Manual override: ${country}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set country';
      setError(errorMessage);
    }
  };

  // Clear cache and re-detect (simplified - no cache to clear)
  const clearCache = () => {
    detectUserCountry();
  };

  // Refresh detection (alias for detectUserCountry)
  const refresh = async () => {
    await detectUserCountry();
  };

  // Computed properties
  const isSupported = SUPPORTED_COUNTRIES.includes(countryCode);
  const displayName = COUNTRY_NAMES[countryCode] || countryCode;
  const confidence = detectionSource === 'timezone' ? 0.8 :
                     detectionSource === 'manual' ? 1.0 : 0.1;

  return {
    countryCode,
    isLoading,
    error,
    isSupported,
    displayName,
    setManualCountry,
    clearCache,
    confidence,
    detectionSource,
    refresh,
  };
}

// Simplified hook for just getting country code
export function useCountryCode(): string {
  const { countryCode } = useCountryDetection();
  return countryCode;
}

// Hook for checking if current country is supported
export function useIsSupportedCountry(): boolean {
  const { isSupported } = useCountryDetection();
  return isSupported;
}